import React from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Typography,
} from "@material-ui/core";
import { useTranslate } from "react-admin";
import InfoIcon from "@material-ui/icons/Info";
import { useFuelWorstPerformance } from "../hooks/useFuelEconomyHooks";
import type { FuelEconomyHookParams } from "../hooks/useFuelEconomyHooks";
import { PerformersTable } from "../common/PerformersTable";
import type { WorstPerformersFuelConsumptionDto } from "../../../common/data/service-proxies";
import "./FuelEconomyReport.css";

type Props = FuelEconomyHookParams;

const formatNumber = (value: number | null | undefined) => {
  if (value == null || Number.isNaN(value)) {
    return "-";
  }
  return value.toFixed(2);
};

export const WorstPerformanceWidget: React.FC<Props> = ({
  fromDate,
  toDate,
  periodType,
}) => {
  const translate = useTranslate();
  const { data, loading: isLoading, error } = useFuelWorstPerformance({
    fromDate,
    toDate,
    periodType,
  });
  const isError = !!error;
  const rows = data ?? [];

  const renderValueCell = (row: WorstPerformersFuelConsumptionDto) => (
    <Typography variant="body2">
      {row.fuelConsumption != null
        ? `${formatNumber(row.fuelConsumption)} ${translate("common.mpg", {
            _: "mpg",
          })}`
        : "-"}
    </Typography>
  );

  return (
    <Card className="widget" style={{ height: "100%", width: "100%" }}>
      <div className="widget-title">
        <div className="widget-title-icon" style={{ color: "#1976d2" }}>
          <InfoIcon />
        </div>
        <div className="widget-title-label">
          {translate("common.worstPerformers", {
            _: "Worst performers",
          })}
        </div>
      </div>
      <CardContent
        style={{
          padding: 16,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Box display="flex" justifyContent="center" py={4}>
            <Typography variant="body2" color="error">
              {translate("common.errorLoadingData", {
                _: "Error loading data",
              })}
            </Typography>
          </Box>
        ) : !rows.length ? (
          <Box display="flex" justifyContent="center" py={4}>
            <Typography variant="body2" color="textSecondary">
              {translate("common.noData", {
                _: "No data",
              })}
            </Typography>
          </Box>
        ) : (
          <PerformersTable<WorstPerformersFuelConsumptionDto>
            rows={rows}
            valueColumnHeader="fuelEconomyReport.fuelConsumption"
            renderValueCell={renderValueCell}
            getRowKey={(row, index) =>
              `${row.vehicleName ?? ""}-${row.driverFullName ?? ""}-${index}`
            }
          />
        )}
      </CardContent>
    </Card>
  );
};

export default WorstPerformanceWidget;
